'use client';
import React from 'react';
import {
  RadialBarChart, RadialBar, ResponsiveContainer, Tooltip,
  PieChart, Pie, Cell, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { DotPerson, getDotColor } from '@/data/dotsData';

interface ReportChartsProps {
  dots: DotPerson[];
}

export default function ReportCharts({ dots }: ReportChartsProps) {
  const blue   = dots.filter(d => getDotColor(d.lastUpdate) === 'blue').length;
  const yellow = dots.filter(d => getDotColor(d.lastUpdate) === 'yellow').length;
  const red    = dots.filter(d => getDotColor(d.lastUpdate) === 'red').length;
  const total  = dots.length || 1;

  const camA   = dots.filter(d => d.cameras === 'A' || d.cameras === 'both').length;
  const camB   = dots.filter(d => d.cameras === 'B' || d.cameras === 'both').length;
  const both   = dots.filter(d => d.cameras === 'both').length;
  const none   = dots.filter(d => d.cameras === 'none').length;

  const statusData = [
    { name: 'Activo', value: blue,   fill: 'var(--dot-blue)' },
    { name: 'Desconectado', value: yellow, fill: 'var(--dot-yellow)' },
    { name: 'Fuera de línea', value: red, fill: 'var(--dot-red)' },
  ];

  const cameraData = [
    { name: 'Solo A', value: dots.filter(d => d.cameras === 'A').length },
    { name: 'Solo B', value: dots.filter(d => d.cameras === 'B').length },
    { name: 'A + B',  value: both },
    { name: 'Sin cámara', value: none },
  ];

  const radialData = [
    { name: 'Activo',  value: Math.round((blue / total) * 100),   fill: 'var(--dot-blue)' },
    { name: 'Desc.',   value: Math.round((yellow / total) * 100), fill: 'var(--dot-yellow)' },
    { name: 'Offline', value: Math.round((red / total) * 100),    fill: 'var(--dot-red)' },
  ];

  const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ name: string; value: number }> }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-card border border-border rounded-lg px-3 py-2 shadow-xl text-xs">
        <p className="font-semibold text-foreground">{payload[0].name}</p>
        <p className="text-muted-foreground">{payload[0].value} personas</p>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      {/* Status pie */}
      <div className="bg-card border border-border rounded-xl p-4">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Distribución de estado</p>
        <ResponsiveContainer width="100%" height={160}>
          <PieChart>
            <Pie
              data={statusData}
              cx="50%"
              cy="50%"
              innerRadius={45}
              outerRadius={70}
              paddingAngle={3}
              dataKey="value"
            >
              {statusData.map((entry, i) => (
                <Cell key={`cell-status-${i}`} fill={entry.fill} stroke="transparent" />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend
              iconType="circle"
              iconSize={8}
              formatter={(value) => <span className="text-xs text-muted-foreground">{value}</span>}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Camera bar */}
      <div className="bg-card border border-border rounded-xl p-4">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Asignaciones de cámara</p>
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={cameraData} barSize={28}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} width={20} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="value" radius={[4, 4, 0, 0]} fill="var(--primary)" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Radial coverage */}
      <div className="bg-card border border-border rounded-xl p-4">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Cobertura por estado (%)</p>
        <ResponsiveContainer width="100%" height={160}>
          <RadialBarChart
            cx="50%"
            cy="50%"
            innerRadius={25}
            outerRadius={70}
            barSize={14}
            data={radialData}
            startAngle={90}
            endAngle={-270}
          >
            <RadialBar
              dataKey="value"
              cornerRadius={4}
              background={{ fill: 'var(--muted)' }}
            />
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                return (
                  <div className="bg-card border border-border rounded-lg px-3 py-2 shadow-xl text-xs">
                    <p className="font-semibold text-foreground">{payload[0].payload.name}</p>
                    <p className="text-muted-foreground">{payload[0].value}%</p>
                  </div>
                );
              }}
            />
            <Legend
              iconType="circle"
              iconSize={8}
              formatter={(value) => <span className="text-xs text-muted-foreground">{value}</span>}
            />
          </RadialBarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}